import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { NAV, SITE } from "../../data/site";
import { Wordmark, ArcButton } from "./Brand";

export const Header = () => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const solid = scrolled || !isHome;
  const onDark = isHome && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-700 ${
        solid ? "bg-chalk/95 backdrop-blur-sm border-b border-charcoal/10" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20 md:h-24">
        {isHome && !scrolled ? (
          <span aria-hidden="true" data-testid="logo-hidden" />
        ) : (
          <Link to="/" data-testid="logo-link" aria-label="Arc & Hollow — home">
            <Wordmark h="h-12 md:h-[4.5rem]" />
          </Link>
        )}

        <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-xs uppercase tracking-overline font-medium transition-colors duration-300 ${
                  onDark ? "text-chalk/90 hover:text-chalk" : "text-charcoal/70 hover:text-charcoal"
                } ${isActive ? (onDark ? "text-chalk" : "text-charcoal") : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <ArcButton to="/contact" variant="outline" onDark={onDark} className="!px-6 !py-3" data-testid="nav-cta">
            Start a conversation
          </ArcButton>
          <a
            href={SITE.instagramUrl}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Visit Arc & Hollow on Instagram"
            data-testid="nav-instagram"
            className={`ml-1 -mr-1 p-2 rounded-full transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${onDark ? "text-chalk/70 hover:text-chalk focus-visible:ring-chalk" : "text-charcoal/55 hover:text-charcoal focus-visible:ring-charcoal"}`}
          >
            <Instagram size={18} strokeWidth={1.6} />
          </a>
        </nav>

        <button
          className="lg:hidden p-2 -mr-2"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="menu-open"
        >
          <Menu className={onDark ? "text-chalk" : "text-charcoal"} size={26} strokeWidth={1.4} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-charcoal text-chalk lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="container-x flex items-center justify-between h-20">
              <Wordmark h="h-12" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="menu-close" className="p-2 -mr-2">
                <X size={26} strokeWidth={1.4} />
              </button>
            </div>
            <nav className="container-x mt-10 flex flex-col gap-2" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="block font-serif text-4xl py-4 border-b border-chalk/15"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <ArcButton to="/contact" variant="primary" onDark className="mt-8 w-full" data-testid="mobile-nav-cta">
                Start a conversation
              </ArcButton>
              <a
                href={SITE.instagramUrl}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Visit Arc & Hollow on Instagram"
                data-testid="mobile-nav-instagram"
                className="mt-8 pt-8 border-t border-chalk/15 flex items-center gap-4 py-3 text-2xl font-serif text-chalk/90 hover:text-chalk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chalk rounded-sm"
              >
                <Instagram size={24} strokeWidth={1.5} />
                <span>Instagram</span>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
